import { useTranslation } from "react-i18next";

import { SUPPORTED_LOCALES, type SupportedLocale } from "@/core/i18n/config";
import { Carte, EnTeteDePanneau } from "@/shared/ui";

import { ENTETE_ECRAN } from "../enteteEcran";

interface LanguageStepProps {
  onChosen: () => void;
}

/**
 * Écran pré-wizard : sélection de langue.
 *
 * S'affiche UNIQUEMENT si l'utilisateur n'a jamais explicitement choisi
 * sa langue (`cockpit.locale.explicit` absent du localStorage). i18next
 * a déjà auto-détecté une langue via navigator, mais cette détection peut
 * être fausse pour un user étranger sur un OS anglais — on lui demande
 * une fois pour ne pas qu'il bloque sur un onboarding incompréhensible.
 *
 * Pas de stepper / chrome : c'est une étape technique, pas du flow métier.
 * Le titre + sous-titre sont affichés dans les 3 langues simultanément
 * pour que tout le monde comprenne sans dépendre de la langue détectée.
 *
 * Drapeaux : SVG inline (pas d'emoji). Microsoft Windows ne rend pas les
 * flag emoji (🇫🇷 → "FR" lettres). SVG = même rendu cross-OS.
 */
const LANG_LABELS: Record<SupportedLocale, string> = {
  fr: "Français",
  en: "English",
  es: "Español",
  it: "Italiano",
};

const LANG_ORDER: ReadonlyArray<SupportedLocale> = ["fr", "en", "es", "it"];

function FlagFR() {
  return (
    <svg viewBox="0 0 3 2" className="h-6 w-9 shrink-0" aria-hidden="true">
      <rect width="1" height="2" fill="#0055A4" />
      <rect width="1" height="2" x="1" fill="#FFFFFF" />
      <rect width="1" height="2" x="2" fill="#EF4135" />
    </svg>
  );
}

function FlagES() {
  return (
    <svg viewBox="0 0 3 2" className="h-6 w-9 shrink-0" aria-hidden="true">
      <rect width="3" height="2" fill="#AA151B" />
      <rect width="3" height="1" y="0.5" fill="#F1BF00" />
    </svg>
  );
}

function FlagGB() {
  return (
    <svg viewBox="0 0 60 30" className="h-6 w-9 shrink-0" aria-hidden="true">
      <clipPath id="cockpit-flag-gb">
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <path d="M0,0 h60 v30 H0 z" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
      <path
        d="M0,0 L60,30 M60,0 L0,30"
        clipPath="url(#cockpit-flag-gb)"
        stroke="#C8102E"
        strokeWidth="4"
      />
      <path d="M30,0 v30 M0,15 h60" stroke="#FFFFFF" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}

function FlagIT() {
  return (
    <svg viewBox="0 0 3 2" className="h-6 w-9 shrink-0" aria-hidden="true">
      <rect width="1" height="2" fill="#008C45" />
      <rect width="1" height="2" x="1" fill="#F4F5F0" />
      <rect width="1" height="2" x="2" fill="#CD212A" />
    </svg>
  );
}

const FLAGS = {
  fr: FlagFR,
  en: FlagGB,
  es: FlagES,
  it: FlagIT,
} as const;

export function LanguageStep({ onChosen }: LanguageStepProps) {
  const { i18n } = useTranslation();

  function handleChoose(locale: SupportedLocale) {
    void i18n.changeLanguage(locale);
    try {
      window.localStorage.setItem("cockpit.locale.explicit", "1");
    } catch {
      // Storage indisponible (mode privé strict) — non bloquant
    }
    onChosen();
  }

  // Triplet de titres affichés en parallèle. Les voisins du focus restent
  // visibles pour ancrer le contexte multilingue.
  const titles = ["Choisis ta langue", "Choose your language", "Elige tu idioma"];

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-canvas px-4 py-10">
      <div className="w-full max-w-[440px]">
        <EnTeteDePanneau
          titre={titles[0]}
          sousTitre={
            <>
              {titles.slice(1).map((t) => (
                <span key={t} className="block">
                  {t}
                </span>
              ))}
            </>
          }
          className={`${ENTETE_ECRAN} mb-7`}
        />

        <div className="flex flex-col gap-3">
          {LANG_ORDER.map((locale) => {
            const Flag = FLAGS[locale];
            const native = LANG_LABELS[locale];
            return (
              <Carte
                key={locale}
                remplissage="lg"
                onClick={() => handleChoose(locale)}
                className="flex items-center gap-4 text-[18px] font-medium"
              >
                <Flag />
                <span>{native}</span>
              </Carte>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function hasUserChosenLocale(): boolean {
  try {
    return window.localStorage.getItem("cockpit.locale.explicit") === "1";
  } catch {
    return true;
  }
}

export const SUPPORTED_LOCALES_FOR_ONBOARDING = SUPPORTED_LOCALES;
